import type { BrowserProvider } from '@coti-io/coti-ethers';
import React, { useState, useCallback, useEffect } from 'react';

import { ConfirmationModal } from './components/ConfirmationModal';
import {
  TokenDetailsContainer,
  TokenDetailsContent,
  TokenDetailsRow,
  TokenDetailsLabel,
  TokenDetailsValue,
  TokenDetailsLink,
  BalanceContainer,
  BalanceTitle,
  TokenBalanceRow,
  TokenBalanceLeft,
  TokenBalanceLogoBox,
  TokenBalanceLogoBig,
  TokenBalanceName,
  TokenBalanceRight,
  TokenBalanceAmount,
  TokenBalanceSymbol,
  SendButton,
  TokenCircle,
  TokenNameRow,
  TokenNameText,
  AddressBadge,
  AddressCopyButton,
  IconButton,
  MenuDropdown,
  MenuItem,
  HeaderBar,
} from './styles';
import { HeaderBarSlotLeft, HeaderBarSlotRight } from './styles/transfer';
import ArrowBack from '../../assets/arrow-back.svg';
import CopyIcon from '../../assets/copy.svg';
import CopySuccessIcon from '../../assets/copy-success.svg';
import VerticalMenuIcon from '../../assets/icons/vertical-menu.svg';
import { CotiLogo } from '../../assets/icons';
import TrashIcon from '../../assets/icons/trash.svg';
import { useChainId } from 'wagmi';
import { useSnap } from '../../hooks/SnapContext';
import { getNetworkConfig } from '../../config/networks';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { useDropdown } from '../../hooks/useDropdown';
import { useImportedTokens } from '../../hooks/useImportedTokens';
import { useTokenOperations } from '../../hooks/useTokenOperations';
import type { ImportedToken } from '../../types/token';
import {
  truncateBalance,
  formatTokenSymbol,
  formatBalance,
  formatTokenBalance,
} from '../../utils/formatters';
import { formatAddressForDisplay } from '../../utils/tokenValidation';

type TokenDetailModalProps = {
  token: ImportedToken | null;
  open: boolean;
  onClose: () => void;
  setActiveTab: React.Dispatch<React.SetStateAction<'tokens' | 'nfts'>>;
  setSelectedToken: React.Dispatch<React.SetStateAction<ImportedToken | null>>;
  provider: BrowserProvider;
  cotiBalance?: string;
  aesKey?: string | null | undefined;
  onSendClick?: (token: ImportedToken) => void;
};

const TokenDetails: React.FC<TokenDetailModalProps> = ({
  token,
  open,
  onClose,
  provider,
  cotiBalance,
  aesKey,
  onSendClick,
}) => {
  const chainId = useChainId();
  const networkConfig = getNetworkConfig(chainId);
  const networkName = networkConfig.name;
  const { userAESKey } = useSnap();
  const { decryptERC20Balance } = useTokenOperations(provider);
  const { importedTokens } = useImportedTokens();
  const [decryptedBalance, setDecryptedBalance] = useState<string>('');
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const { copied, copyToClipboard } = useCopyToClipboard();
  const { removeToken } = useImportedTokens();
  const menuDropdown = useDropdown();
  const closeMenuDropdown = menuDropdown.close;
  const [showHideModal, setShowHideModal] = useState(false);

  const effectiveAESKey = aesKey || userAESKey;

  const decryptBalance = useCallback(async () => {
    if (!token) {
      return;
    }
    if (!token.address) {
      setDecryptedBalance(cotiBalance || '0');
      return;
    }
    if (!effectiveAESKey) {
      setDecryptedBalance('(encrypted)');
      return;
    }
    setIsDecrypting(true);
    try {
      const balance = await decryptERC20Balance(
        token.address,
        effectiveAESKey,
        token.decimals,
      );
      setDecryptedBalance(`${balance}`);
    } catch (error) {
      setDecryptedBalance('(encrypted)');
    } finally {
      setIsDecrypting(false);
    }
  }, [token, effectiveAESKey, decryptERC20Balance, cotiBalance]);

  useEffect(() => {
    if (open && token) {
      decryptBalance();
    }
  }, [open, token, decryptBalance]);

  useEffect(() => {
    if (token?.symbol === 'COTI') {
      closeMenuDropdown();
    }
  }, [token?.symbol, closeMenuDropdown]);

  const handleCopy = useCallback(
    (text: string) => {
      copyToClipboard(text);
    },
    [copyToClipboard],
  );

  const handleRemoveToken = useCallback(() => {
    setShowHideModal(true);
    menuDropdown.close();
  }, [menuDropdown]);

  const handleHideToken = useCallback(() => {
    if (token) {
      removeToken(token.address);
      setShowHideModal(false);
      onClose();
    }
  }, [token, removeToken, onClose]);

  const handleCancelHide = useCallback(() => {
    setShowHideModal(false);
  }, []);

  if (!open || !token) {
    return null;
  }

  const importedToken = importedTokens.find(
    (importedToken) =>
      importedToken.address.toLowerCase() === token.address.toLowerCase() ||
      (importedToken.name === token.name &&
        importedToken.symbol === token.symbol),
  );

  const tokenAddress = importedToken?.address || token.address;

  const shortAddress = tokenAddress
    ? formatAddressForDisplay(tokenAddress)
    : 'Native Token';

  const formattedBalance = isDecrypting
    ? 'Loading...'
    : (() => {
        if (
          decryptedBalance &&
          decryptedBalance !== '0' &&
          decryptedBalance !== '(encrypted)'
        ) {
          const decimals = token.symbol === 'COTI' ? 18 : token.decimals || 18;
          return formatTokenBalance(decryptedBalance, decimals);
        }
        return formatBalance(decryptedBalance);
      })();

  const hasBalance =
    decryptedBalance &&
    decryptedBalance !== '0' &&
    decryptedBalance !== '(encrypted)' &&
    decryptedBalance !== '';

  return (
    <>
      <TokenDetailsContainer>
        <HeaderBar>
          <HeaderBarSlotLeft>
            <IconButton onClick={onClose} type="button" aria-label="Go back">
              <ArrowBack />
            </IconButton>
          </HeaderBarSlotLeft>
          <HeaderBarSlotRight style={{ position: 'relative' }}>
            {token?.symbol !== 'COTI' ? (
              <>
                <IconButton
                  onClick={menuDropdown.toggle}
                  selected={menuDropdown.isOpen}
                  type="button"
                  aria-label="Menu"
                >
                  <VerticalMenuIcon />
                </IconButton>

                {menuDropdown.isOpen && (
                  <MenuDropdown ref={menuDropdown.ref}>
                    <MenuItem onClick={handleRemoveToken} type="button">
                      <TrashIcon />
                      Hide {token.symbol}
                    </MenuItem>
                  </MenuDropdown>
                )}
              </>
            ) : null}
          </HeaderBarSlotRight>
        </HeaderBar>

        <BalanceContainer>
          <BalanceTitle>Your balance</BalanceTitle>
          <TokenBalanceRow>
            <TokenBalanceLeft>
              <TokenBalanceLogoBox>
                <TokenBalanceLogoBig>
                  {token.symbol === 'COTI' ? <CotiLogo /> : token.symbol[0]}
                </TokenBalanceLogoBig>
              </TokenBalanceLogoBox>
              <TokenBalanceName>{token.name}</TokenBalanceName>
            </TokenBalanceLeft>
            <TokenBalanceRight>
              <TokenBalanceAmount title={formattedBalance}>
                {truncateBalance(formattedBalance)}
              </TokenBalanceAmount>
              <TokenBalanceSymbol>
                {token.address ? formatTokenSymbol(token.symbol) : token.symbol}
              </TokenBalanceSymbol>
            </TokenBalanceRight>
          </TokenBalanceRow>
        </BalanceContainer>

        <TokenDetailsContent>
          <BalanceTitle>Token details</BalanceTitle>
          <TokenDetailsRow>
            <TokenDetailsLabel>Network</TokenDetailsLabel>
            <TokenDetailsValue>
              <TokenNameRow>
                <TokenCircle>
                  <CotiLogo />
                </TokenCircle>
                <TokenNameText>{networkName}</TokenNameText>
              </TokenNameRow>
            </TokenDetailsValue>
          </TokenDetailsRow>
          {token.symbol !== 'COTI' && (
            <TokenDetailsRow>
              <TokenDetailsLabel>Contract address</TokenDetailsLabel>
              <AddressBadge>
                <TokenDetailsLink
                  href={`${networkConfig.explorerUrl}/address/${tokenAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {shortAddress}
                </TokenDetailsLink>
                <AddressCopyButton onClick={() => handleCopy(tokenAddress)}>
                  {copied ? <CopySuccessIcon /> : <CopyIcon />}
                </AddressCopyButton>
              </AddressBadge>
            </TokenDetailsRow>
          )}
          <TokenDetailsRow>
            <TokenDetailsLabel>Token decimals</TokenDetailsLabel>
            <TokenDetailsValue>{token.decimals}</TokenDetailsValue>
          </TokenDetailsRow>
        </TokenDetailsContent>
        <SendButton
          onClick={() => onSendClick && onSendClick(token)}
          disabled={!hasBalance}
        >
          Send
        </SendButton>
      </TokenDetailsContainer>

      <ConfirmationModal
        isOpen={showHideModal}
        onClose={handleCancelHide}
        onConfirm={handleHideToken}
        title="Hide Token"
        message={`You can add this token back in the future by going to "Import token" in your accounts options menu.`}
        address={tokenAddress}
        symbol={`${token.symbol}`}
        confirmText="Hide"
        confirmButtonColor="#e53935"
        cancelText="Cancel"
      />
    </>
  );
};

export default TokenDetails;
